/**
 * PURPOSE: Coordinates two DealXG socket-event concerns for the sales-checkin shell.
 *
 *   (1) Defer the shared DealRefreshModal — `deferDealRefresh` swallows an incoming deal-refresh
 *       socket event while the shopper is mid Trade-In or on the Customer Details / Insurance
 *       card (so it does not interrupt them), and `releaseDeferredRefresh` shows the deferred
 *       modal once every one of those gates has cleared. This is the "before + after `next()`"
 *       half of the middleware.
 *
 *   (2) Process the incoming DealXG — `processDealXgSocketEvent` runs the DealXG-based processors
 *       (e.g. `syncDealRefId`). This is the EXTENSION POINT: to react to more of the incoming DealXG,
 *       add a processor under `./handlers/dealXgSocketProcessors` — no change to this file needed.
 *
 * This middleware MUST be positioned immediately before `dealRefreshModalMiddleware` in the chain so
 * `deferDealRefresh` can swallow the socket event before the shared modal middleware would show it.
 */

// externals
import type { Middleware } from 'redux';
import { LOCATION_CHANGE } from 'connected-react-router';

// libraries
import { NavigationActions } from '@makemydeal/dr-activities-common';

// store
import { DealXgSocketActions } from '../socket/dealXgSocketActionTypes';
import { Actions } from '../actionTypes/actionTypes';
import { deferDealRefresh, releaseDeferredRefresh } from './helpers';
import { processDealXgSocketEvent } from './processors/processors';

export const dealRefreshDeferMiddleware: Middleware = (store) => (next) => (action: { type: string; payload?: any }) => {
    if (action.type === DealXgSocketActions.SOCKET_EVENT_RECEIVED) {
        // React to the incoming DealXG (extensible).
        processDealXgSocketEvent(store, action);

        // Defer + swallow the refresh modal while in a GTL-deferred screen
        if (deferDealRefresh(store, action)) {
            return; // event swallowed — do not forward it to the shared dealRefreshModalMiddleware
        }
    }

    next(action);

    // Release a previously deferred refresh once every gate above has cleared — the shopper leaves
    // Trade-In/Financing (NAVIGATE_TO), closes the Customer Details slideout
    // (SET_CUSTOMER_CARD_STATE), or — under full-scroll only, see `releaseDeferredRefresh` — pops
    // back from a takeover via "Back to My Deal" or browser Back (LOCATION_CHANGE, TA4265943).
    if (
        action.type === NavigationActions.NAVIGATE_TO ||
        action.type === Actions.SET_CUSTOMER_CARD_STATE ||
        action.type === LOCATION_CHANGE
    ) {
        releaseDeferredRefresh(store, action);
    }
};
